"use client";

import { deleteCookie, setCookie, getCookie } from "cookies-next";
import { useEffect } from "react";
import { CookieKeys } from "@/constants/cookiesEnums";
import { browserCookieAttrs } from "@/utils/functions/storefront-cookie-options";

/**
 * Detects a forced cookie-clear (`${host}--cookie_clear_ts` in Redis moving ahead of the
 * visitor's `branch_clear_ts`) and drops the stale `branch_id`.
 *
 * This component is mounted in TWO places — the root layout (every page) and
 * /company-error — and it behaves differently in each, because the cost profile is
 * completely different:
 *
 *  - on `/company-error`: the page is idle by definition, waiting for the shop to come
 *    back. Poll on an interval with backoff so recovery is actually noticed.
 *  - everywhere else: check at most ONCE per tab session. `src/proxy.ts` already
 *    recomputes `mustClearCookies` server-side on EVERY request and syncs the cookies in
 *    the same pass, so a navigating visitor is already covered without any client call.
 *    Firing per page load (the previous behaviour) bought nothing and cost one request
 *    per cold load on every healthy storefront.
 *
 * Both paths are strictly fewer requests than the previous fire-on-every-mount version.
 */

const POLL_BASE_MS = 30_000; // 30s between polls on the error page
const POLL_MAX_MS = 5 * 60_000; // backoff ceiling
const LEASE_KEY = "ccc:poll-lease"; // cross-tab lease so N tabs cost ~1 poller
const LEASE_TTL_MS = 45_000; // > POLL_BASE_MS, so the holder keeps renewing it
const SESSION_KEY = "ccc:checked"; // once-per-session stamp for non-error pages
const BC_NAME = "ccc:clear"; // fan the clear signal out to sibling tabs

type Lease = { owner: string; ts: number };

/**
 * A tab may poll if the lease is unheld, stale, or already its own. Ownership is what
 * makes renewal work: a bare timestamp would let the holder lock itself out, since it
 * comes back to renew (every POLL_BASE_MS) before its own lease has expired.
 */
function acquireOrRefreshLease(selfId: string): boolean {
  try {
    const raw = window.localStorage?.getItem(LEASE_KEY);
    const lease = raw ? (JSON.parse(raw) as Lease) : null;
    const now = Date.now();
    const isMine = lease?.owner === selfId;
    const isStale =
      !lease || !Number.isFinite(lease.ts) || now - lease.ts > LEASE_TTL_MS;
    if (isMine || isStale) {
      window.localStorage?.setItem(
        LEASE_KEY,
        JSON.stringify({ owner: selfId, ts: now } satisfies Lease),
      );
      return true;
    }
    return false;
  } catch {
    // Private mode / quota / no localStorage — degrade to polling. Never block recovery.
    return true;
  }
}

/** Only the holder may release, so one tab unmounting cannot free another's lease. */
function releaseLease(selfId: string) {
  try {
    const raw = window.localStorage?.getItem(LEASE_KEY);
    const lease = raw ? (JSON.parse(raw) as Lease) : null;
    if (lease?.owner === selfId) window.localStorage?.removeItem(LEASE_KEY);
  } catch {
    /* nothing to do — a stale lease ages out via LEASE_TTL_MS */
  }
}

export default function CookieClearCheck({
  /**
   * Set by the error page only. Must be an explicit prop, NOT sniffed from
   * `window.location.pathname`: the proxy reaches the error page via a *rewrite*, which
   * preserves the original URL, so a suspended visitor sitting on the error page still
   * has `/products` in the address bar. Sniffing the path meant the recovery poller never
   * ran on precisely the page that exists to wait for recovery.
   */
  pollForRecovery = false,
}: {
  pollForRecovery?: boolean;
}) {
  useEffect(() => {
    const isErrorPage = pollForRecovery;
    // Normal pages: one check per tab session. The stamp is written when the check
    // actually runs (see poll), not here — writing it up front burned the session for a
    // tab that started hidden and therefore never checked at all.
    if (!isErrorPage) {
      try {
        if (window.sessionStorage?.getItem(SESSION_KEY)) return;
      } catch {
        /* no sessionStorage — fall through and check once for this mount */
      }
    }

    // Identifies this tab for the cross-tab lease. Deliberately NOT crypto.randomUUID:
    // that needs a secure context and is undefined on a plain-http host, and a lease id
    // has no security requirement — a collision just means two tabs poll for one cycle.
    const selfId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;
    let stopped = false;
    let failures = 0;

    const bc =
      typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(BC_NAME) : null;

    const stopForever = () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      releaseLease(selfId);
    };

    // Allowed in both modes: a one-shot check that arrives while the tab is hidden still
    // needs to be retried when it becomes visible. What differs is whether a COMPLETED
    // check reschedules (see poll).
    const schedule = (delay: number) => {
      if (stopped) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(poll, delay);
    };

    const applyClear = (clearTimestamp: number, broadcast: boolean) => {
      // Same attrs as the proxy's write, or both are rejected inside a cross-site frame.
      deleteCookie(CookieKeys.branch_id, { path: "/", ...browserCookieAttrs() });
      setCookie(CookieKeys.branch_clear_ts, String(clearTimestamp), {
        maxAge: 2592000, // 30 days
        path: "/",
        ...browserCookieAttrs(),
      });
      // Tell sibling tabs so they recover too, without each having polled.
      if (broadcast) bc?.postMessage({ type: "clear", clearTimestamp });
      stopForever();
      if (window.location.pathname.includes("/company-error")) {
        window.location.href = "/";
      } else {
        window.location.reload();
      }
    };

    if (bc) {
      bc.onmessage = (e) => {
        if (!stopped && e.data?.type === "clear" && e.data?.clearTimestamp) {
          applyClear(Number(e.data.clearTimestamp), false); // no echo
        }
      };
    }

    async function poll() {
      if (stopped) return;
      // Don't burn polls in a backgrounded tab; visibilitychange re-triggers immediately.
      if (document.visibilityState === "hidden") return schedule(POLL_BASE_MS);
      // Another tab owns this cycle.
      if (isErrorPage && !acquireOrRefreshLease(selfId)) return schedule(POLL_BASE_MS);

      // Committing to the single check now that it is actually running.
      if (!isErrorPage) {
        try {
          window.sessionStorage?.setItem(SESSION_KEY, "1");
        } catch {
          /* no sessionStorage — worst case this tab checks once more next navigation */
        }
      }

      controller = new AbortController();
      try {
        const res = await fetch(`/api/check-clear-cookies?t=${Date.now()}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // Body shape must stay exactly as the route expects.
          body: JSON.stringify({
            host: window.location.host,
            href: window.location.href,
            pathname: window.location.pathname,
            clientClearTsRaw: getCookie(CookieKeys.branch_clear_ts),
          }),
          cache: "no-store",
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        failures = 0; // any answer at all resets the backoff, not just a clear signal
        if (data.shouldClearCookies && data.clearTimestamp) {
          applyClear(Number(data.clearTimestamp), true);
          return;
        }
        // Only the error page keeps polling. On a normal page the check is done: the
        // proxy re-evaluates the cookie-clear server-side on every request anyway.
        if (isErrorPage) schedule(POLL_BASE_MS);
      } catch (err) {
        // An unmount abort is not a failure and must not inflate the backoff.
        if ((err as Error)?.name === "AbortError") return;
        if (!isErrorPage) return; // one shot; don't retry on a normal page
        failures += 1;
        schedule(
          Math.min(POLL_BASE_MS * 2 ** Math.max(0, failures - 1), POLL_MAX_MS),
        );
      }
    }

    // Registered in BOTH modes: a one-shot check on a tab that mounted hidden would
    // otherwise never run at all.
    const onVisibilityChange = () => {
      if (!stopped && document.visibilityState === "visible") schedule(0);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    void poll();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      controller?.abort();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      bc?.close();
    };
  }, [pollForRecovery]);

  return null;
}
